import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";

// '../../' is the path to src/
import config from '../../config';
import handleResponse from '../../Components/FormComponent/FormComponentHelper';

export function ForgotPasswordPage() {
    const navigate = useNavigate();
    const [input, setInput] = useState({ email: '' });
    const [error, setError] = useState({});
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    function handleChange(e) {
        const { name, value } = e.target;
        setInput(prev => ({
            ...prev,
            [name]: value
        }));
        validateInput(e);
    }

    const validateInput = e => {
        let { name, value } = e.target;
        setError(prev => {
            const stateObj = { ...prev, [name]: "" };
            delete stateObj[name];
            return { ...stateObj, ..._validateInput(name, value) }
        });
    }

    function handleSubmit(e) {
        e.preventDefault();

        if (!_isFormValid()) return;

        setLoading(true);
        setErrorMessage('');

        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input)
        };

        fetch(`${config.apiUrl}/api/users/forgot-password`, requestOptions)
            .then((response) => {
                return handleResponse(response)
            })
            .then(
                (response) => {
                    setLoading(false);
                    toast.success(response.message || 'If an account with that email exists, a password reset link has been sent.');
                    navigate('/login');
                },
                (error) => {
                    setErrorMessage(error);
                    setLoading(false);
                }
            );
    }

    function _isFormValid() {
        let combinedState = {};

        for (let key in input) {
            combinedState = { ...combinedState, ..._validateInput(key, input[key]) };
        }

        setError(prev => {
            return { ...prev, ...combinedState }
        });

        return Object.keys(combinedState).length === 0;
    }

    function _validateInput(name, value) {
        const stateObj = {};

        switch (name) {
            case "email":
                if (!value) {
                    stateObj[name] = "Please enter your email address.";
                } else if (!/\S+@\S+\.\S+/.test(value)) {
                    stateObj[name] = "Please enter a valid email address.";
                }
                break;
            default:
                break;
        }
        return stateObj;
    }

    return (
        <div className="min-vh-100 d-flex align-items-center" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-md-6 col-lg-4">
                        <div className="modern-card text-center mb-4">
                            <div className="card-body p-5">
                                <div className="mb-4">
                                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                         style={{width: '4rem', height: '4rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
                                        <i className="bi bi-key-fill text-white fs-3"></i>
                                    </div>
                                    <h2 className="fw-bold mb-2" style={{color: '#1e293b'}}>Forgot Password?</h2>
                                    <p className="text-muted mb-0">No worries, we'll send you reset instructions</p>
                                </div>

                                {errorMessage && (
                                    <div className="alert alert-danger rounded-3 mb-4" role="alert">
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                        {errorMessage}
                                    </div>
                                )}

                                <form name="form" onSubmit={handleSubmit}>
                                    <div className={'mb-4' + (error.email ? ' has-error' : '')}>
                                        <div className="form-floating">
                                            <input
                                                type="email"
                                                className={'form-control ' + (error.email ? 'is-invalid' : '')}
                                                id="email"
                                                name="email"
                                                value={input.email}
                                                onChange={handleChange}
                                                onBlur={validateInput}
                                                autoComplete="email"
                                                placeholder="Email Address"
                                            />
                                            <label htmlFor="email">Email Address</label>
                                            {error.email && <div className='invalid-feedback'>{error.email}</div>}
                                        </div>
                                        <div className="form-text mt-2 text-muted small">
                                            <i className="bi bi-info-circle me-1"></i>
                                            Enter the email address associated with your account
                                        </div>
                                    </div>

                                    <div className="d-grid gap-2 mb-4">
                                        <button
                                            type="submit"
                                            className="btn btn-modern btn-lg"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Sending Reset Link...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-envelope me-2"></i>
                                                    Send Reset Link
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    <div className="text-center">
                                        <Link to="/login" className="btn btn-outline-modern">
                                            <i className="bi bi-arrow-left me-2"></i>
                                            Back to Login
                                        </Link>
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div className="text-center">
                            <p className="text-white-50 small mb-0">
                                <i className="bi bi-shield-check me-1"></i>
                                We'll never share your email with anyone else
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}